"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { FiBriefcase, FiMapPin, FiClock, FiUsers } from "react-icons/fi";
import { getActiveJobs } from "@/api/careersService";
import { JobPost } from "@/models/api/careers";
import formStyles from "@/components/enquiryForm/EnquiryForm.module.css";
import styles from "./CareersSection.module.css";

const formatPostedDate = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const CareersSection: React.FC = () => {
  const router = useRouter();
  const [jobs, setJobs] = useState<JobPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    getActiveJobs()
      .then((data) => {
        if (isMounted) setJobs(data);
      })
      .catch(() => {
        if (isMounted) setError("We couldn't load current job openings. Please try again shortly.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleApply = (jobId: string) => {
    router.push(`/careers/details?id=${jobId}`);
  };

  return (
    <section className="pb-100 pt-100">
      <div className="container">
        <div className={`${formStyles.sectionHeader} ${styles.sectionHeading}`}>
          <h3 className={formStyles.title}>Current Openings</h3>
        </div>

        {loading && (
          <div className={styles.loaderWrapper}>
            <div className={formStyles.loader}></div>
          </div>
        )}

        {!loading && error && (
          <div className={`text-center ${styles.errorWrapper}`}>
            <div className={styles.errorIconContainer}>
              <span className={styles.errorIconText}>!</span>
            </div>
            <h3 className={styles.errorTitle}>Unable to Load Job Openings</h3>
          </div>
        )}

        {!loading && !error && jobs.length === 0 && (
          <div className={`text-center ${styles.emptyWrapper}`}>
            <div className={styles.emptyIconContainer}>
              <FiBriefcase size={40} />
            </div>
            <h3 className={styles.emptyTitle}>No Openings Right Now</h3>
            <p className={styles.emptyText}>
              We don't have any open positions at the moment. Please check back soon.
            </p>
          </div>
        )}

        {!loading && !error && jobs.length > 0 && (
          <div className={styles.cardsGrid}>
            {jobs.map((job) => (
              <div key={job.id} className={`${formStyles.contentbox} ${styles.jobCard}`}>
                <h3 className={styles.jobTitle}>{job.title}</h3>

                <div className={styles.badgeRow}>
                  {job.department && (
                    <span className={styles.badge}>
                      <FiBriefcase size={14} /> {job.department}
                    </span>
                  )}
                  {job.location && (
                    <span className={styles.badge}>
                      <FiMapPin size={14} /> {job.location}
                    </span>
                  )}
                  {job.type && (
                    <span className={styles.badge}>
                      <FiClock size={14} /> {job.type}
                    </span>
                  )}
                </div>

                {job.description && <p className={styles.description}>{job.description}</p>}

                <div className={styles.cardFooter}>
                  <div className={styles.metaRow}>
                    {job.posted && <>Posted {formatPostedDate(job.posted)}</>}
                    {job.numberOfVacancies ? (
                      <>
                        {" "}
                        &middot; <FiUsers size={13} /> {job.numberOfVacancies} opening
                        {job.numberOfVacancies > 1 ? "s" : ""}
                      </>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className={formStyles.primaryBtn}
                    onClick={() => handleApply(job.id)}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CareersSection;
